// src/providers/ClickableProvider.tsx
import React, { createContext, useContext, ReactNode, useCallback, MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';

interface ClickableContextType {
    makeClickable: (url: string, customHandler?: (e: MouseEvent) => void) => {
        onClick: (e: MouseEvent) => void;
        onMouseUp: (e: MouseEvent) => void;
        'data-clickable': boolean;
    };
}

const ClickableContext = createContext<ClickableContextType | null>(null);

export const useClickable = () => {
    const context = useContext(ClickableContext);
    if (!context) {
        throw new Error('useClickable must be used within ClickableProvider');
    }
    return context;
};

interface ClickableProviderProps {
    children: ReactNode;
}

export const ClickableProvider: React.FC<ClickableProviderProps> = ({ children }) => {
    const navigate = useNavigate();

    const makeClickable = useCallback((url: string, customHandler?: (e: MouseEvent) => void) => {
        const handleMouseDown = (e: MouseEvent) => {
            // Для колесика мыши предотвращаем стандартное поведение
            if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();

                // Добавляем класс для предотвращения прокрутки
                (e.currentTarget as HTMLElement).classList.add('no-middle-click-scroll');

                return;
            }
        };

        const handleMouseUp = (e: MouseEvent) => {
            // Для колесика мыши
            if (e.button === 1) {
                e.preventDefault();
                e.stopPropagation();

                // Убираем класс
                (e.currentTarget as HTMLElement).classList.remove('no-middle-click-scroll');

                // Открываем ссылку
                const link = document.createElement('a');
                link.href = url.startsWith('http') ? url : window.location.origin + url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                return;
            }
        };

        const handleClick = (e: MouseEvent) => {
            // Ctrl+Click или Cmd+Click
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();

                const link = document.createElement('a');
                link.href = url.startsWith('http') ? url : window.location.origin + url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.style.display = 'none';

                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }

            // Обычный клик левой кнопкой (button === 0)
            if (e.button === 0) { // обычный левый клик
                if (customHandler) customHandler(e);
                if (url && !url.startsWith('http')) {
                    navigate(url);
                } else if (url) {
                    window.location.href = url;
                }
            }
        };

        return {
            onClick: handleClick,
            onMouseDown: handleMouseDown,
            onMouseUp: handleMouseUp,
            'data-clickable': true as const
        };
    }, [navigate]);

    return (
        <ClickableContext.Provider value={{ makeClickable }}>
            {children}
        </ClickableContext.Provider>
    );
};